import type { ElementType } from 'react';
import tw, { styled } from 'twin.macro';

import type { Align, Justify, Props, Spacing } from './Stack';

export const getAlignStyles = (align?: Align) => {
	switch (align) {
		case 'baseline':
			return tw`items-baseline`;
		case 'center':
			return tw`items-center`;
		case 'end':
			return tw`items-end`;
		case 'start':
			return tw`items-start`;
		case 'stretch':
			return tw`items-stretch`;
	}
};

export const getJustifyStyles = (justify?: Justify) => {
	switch (justify) {
		case 'around':
			return tw`justify-around`;
		case 'between':
			return tw`justify-between`;
		case 'center':
			return tw`justify-center`;
		case 'end':
			return tw`justify-end`;
		case 'evenly':
			return tw`justify-evenly`;
		case 'start':
			return tw`justify-start`;
	}
};

export const getSpacingStyles = (spacing?: Spacing) => {
	switch (spacing) {
		case 'extraTight':
			return tw`gap-1`;
		case 'tight':
			return tw`gap-2`;
		case 'loose':
			return tw`gap-4`;
		case 'mediumLoose':
			return tw`gap-6`;
		case 'extraLoose':
			return tw`gap-8`;
	}
};

/** List resets when polymorphic `as` is an intrinsic `ul` / `ol` / `li`. */
export const getAsStyles = (as: ElementType | undefined) => {
	const tag = typeof as === 'string' ? as : undefined;
	switch (tag) {
		case 'ol':
		case 'ul':
			return tw`m-0 list-none p-0`;
		case 'li':
			return tw`list-none`;
		default:
			return undefined;
	}
};

// Aliasing the "wrap" property because it's a reserved DOM prop
interface StyledProps extends Omit<Props, 'wrap'> {
	shouldWrap?: boolean;
	/** Polymorphic tag; intrinsic `ul` / `ol` / `li` enable list reset styles via `getAsStyles`. */
	as?: ElementType;
}

export const StyledStack = styled.div<StyledProps>(
	({ inline, align, justify, spacing, vertical, shouldWrap, as: asProp }) => [
		inline ? tw`inline-flex` : tw`flex`,
		getAlignStyles(align),
		getJustifyStyles(justify),
		getSpacingStyles(spacing),
		vertical && tw`flex-col`,
		shouldWrap && tw`flex-wrap`,
		getAsStyles(asProp),
	]
);
